import React, { createContext, useContext, useState, useCallback } from 'react';
import { IncomingTranscript } from '@/hooks/useIncomingTranscripts';

type DialogMode = 'preview' | 'edit';

interface IncomingTranscriptDialogContextType {
  isOpen: boolean;
  mode: DialogMode;
  transcript: IncomingTranscript | null;
  openPreview: (transcript: IncomingTranscript) => void;
  openEdit: (transcript: IncomingTranscript) => void;
  closeDialog: () => void;
  setMode: (mode: DialogMode) => void;
  onSuccess?: () => void;
  setOnSuccess: (callback: (() => void) | undefined) => void;
}

const IncomingTranscriptDialogContext = createContext<IncomingTranscriptDialogContextType | undefined>(undefined);

export const IncomingTranscriptDialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<DialogMode>('preview');
  const [transcript, setTranscript] = useState<IncomingTranscript | null>(null);
  const [onSuccess, setOnSuccess] = useState<(() => void) | undefined>(undefined);

  const openPreview = useCallback((t: IncomingTranscript) => {
    setTranscript(t);
    setMode('preview');
    setIsOpen(true);
  }, []);

  const openEdit = useCallback((t: IncomingTranscript) => {
    setTranscript(t);
    setMode('edit');
    setIsOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setIsOpen(false);
    setTranscript(null);
    setMode('preview');
  }, []);

  return (
    <IncomingTranscriptDialogContext.Provider
      value={{
        isOpen,
        mode,
        transcript,
        openPreview,
        openEdit,
        closeDialog,
        setMode,
        onSuccess,
        setOnSuccess,
      }}
    >
      {children}
    </IncomingTranscriptDialogContext.Provider>
  );
};

export const useIncomingTranscriptDialog = () => {
  const context = useContext(IncomingTranscriptDialogContext);
  if (context === undefined) {
    throw new Error('useIncomingTranscriptDialog must be used within an IncomingTranscriptDialogProvider');
  }
  return context;
};
